import { Button, Container, Typography } from '@mui/material';

import { AppDivider } from 'components/core/AppDivider/AppDivider';
import { Page } from 'components/layout/Page/Page';
import { ContactSummary } from 'components/views/pages/Checkout/CheckoutSummary/ContactSummary';
import { StickyWrapper } from 'components/views/pages/Checkout/CheckoutSummary/styles';
import { checkoutRefer, facebookIcon } from 'config/imageUrls';
import { usePropertyInformationQuery } from 'components/views/pages/Checkout/hooks/usePropertyInformationQuery';
import { httpsUrl } from 'config/url';
import { useScreenWidth } from 'webHooks/useScreenWidth';
import { useCheckout } from 'components/views/pages/Checkout/hooks/useCheckout';
import { useLoginState } from 'components/views/pages/_general/useLoginState';
import { BookingSettings } from 'components/views/pages/Checkout/CheckoutStep1/components/BookingSettings';
import { ContactSendMessageForm } from 'components/views/pages/contact/ContactSendMessageForm';
import { CheckoutLoginCreateAccountForm } from 'components/views/pages/Checkout/CheckoutUserFlow/CheckoutLoginCreateAccountForm';
import { CheckoutSummaryProperty } from 'components/views/pages/Checkout/CheckoutSummary/components/CheckoutSummaryProperty';
import { CheckoutSummaryCommonQuestions } from 'components/views/pages/Checkout/CheckoutSummary/components/CheckoutSummaryCommonQuestions';
import { useContactParams } from 'components/views/pages/contact/useContactParams';
import { useContactFixInvalidParams } from 'components/views/pages/contact/useContactFixInvalidParams';
import { ContactBookNowButton } from 'components/views/pages/contact/ContactBookNowButton';
import styled from 'styled-components';
import { CheckoutDownloadMobileApp } from 'components/views/pages/Checkout/CheckoutStep4/components/CheckoutDownloadMobileApp';

const StyledGrid = styled(Grid)`

${props => props.theme.breakpoints.up('md')} {
    padding-right: ${props => props.theme.spacing(8)};
  }
`;
import { getNumberOrZero } from 'helpers/numHelpers';
import Grid from 'components/core/Grid';
import { EMAIL_INFO, PRODUCTION_DOMAIN } from 'config/appconstants';

export enum ContactSteps {
  SendMessage = 0,
  MessageSent = 1,
}

const Contact = () => {
  const { isMd } = useScreenWidth();

  const { startDate, endDate, adults, pChildren, infants, pets, propertyId, step } = useContactParams();
  useContactFixInvalidParams();

  const { userId, loginState } = useLoginState({ propertyId });
  const propertyInfoQuery = usePropertyInformationQuery({ pid: Number(propertyId), enabled: Boolean(propertyId) });

  // for getting the checkout data without having to have guests
  const isCheckoutQueryEnabled = Boolean(propertyId) && Boolean(startDate) && Boolean(endDate)// && Boolean(adults);
  const num_guests = (getNumberOrZero(adults) + getNumberOrZero(pChildren)) || 1;

  const setPropertyUrl = function () {
    window.location.href = `${httpsUrl()}/${propertyId}`
  }

  const { query: checkoutQuery, isInvalidDates } = useCheckout(
    {
      isDirectVacations: false,
      arrival: startDate as string,
      departure: endDate as string,
      guestId: Number(userId),
      num_guests,
      num_pets: Number(pets),
      propertyId: Number(propertyId),
    },
    {
      enabled: isCheckoutQueryEnabled,
      hasToastsEnabled: false,
    },
  );

  const onClickGoBack = () => {
    if (!window) return;
    window.location.href = `${httpsUrl()}/${propertyId}`;
  };

  const isLoading = !propertyInfoQuery.data;
  const isPetFriendly = isLoading ? false : propertyInfoQuery.data?.DATA.IS_PET_FRIENDLY;
  const maxPartySize = propertyInfoQuery.data?.DATA.MAX_CAPACITY;

  return (
    <Page
      appSEOProps={{
        title: 'Contact Host',
        description: 'Contact the host',
        robots: 'noindex,follow',
      }}
      headerProps={{
        borderBottom: true,
        dvLogoFilepath: propertyInfoQuery.data?.DATA.DV_LOGO_FILEPATH,
      }}
      hasNavSection
      navSectionProps={{
        hasBackIcon: true,
        onClickGoBack,
        title: 'Go Back',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {isMd ? (
            <Grid item xs={12}>
              <CheckoutSummaryProperty pid={propertyId} />
              <AppDivider marginVertical={'24px'} />
            </Grid>
          ) : null}

          {Number(step) === ContactSteps.SendMessage ? (
            <Grid item xs={12} md={7} mb={10}>
              <StyledGrid container spacing={2}>
                <Grid item xs={12}>
                  <BookingSettings
                    adults={adults}
                    pChildren={pChildren}
                    infants={infants}
                    endDate={endDate}
                    startDate={startDate}
                    isInvalidDates={isInvalidDates}
                    isLoading={false}
                    isPetFriendly={isPetFriendly}
                    maxPartySize={maxPartySize}
                    pets={pets}
                    dateTitle='What are your rental dates'
                    datePlaceholder='No dates selected (optional)'
                    guestTitle='How many guests?'
                    guestPlaceholder='No guests added (optional)'
                    currency_code='CAD'
                    currency_multiplier={1}
                  />
                </Grid>
                <Grid item xs={12}>
                  <AppDivider marginVertical={'24px'} />
                </Grid>
                {checkoutQuery.data && isCheckoutQueryEnabled && <>
                  <ContactBookNowButton
                    isLoading={!checkoutQuery.data}
                    propertyId={Number(propertyId)}
                    userId={Number(userId)}
                    adults={adults}
                    pChildren={pChildren}
                    infants={infants}
                    pets={pets}
                    startDate={startDate}
                    endDate={endDate}
                    bookingQuoteTotal={checkoutQuery?.data?.DATA?.BOOKING?.TOTAL}
                  />
                  <Grid item xs={12}>
                    <AppDivider marginVertical={'24px'} />
                  </Grid>
                </>}
                {loginState === 'loggedin' ? (
                  <ContactSendMessageForm
                    propertyId={Number(propertyId)}
                    guestId={Number(userId)}
                    adults={adults}
                    pChildren={pChildren}
                    infants={infants}
                    pets={pets}
                    startDate={startDate}
                    endDate={endDate}
                  />
                ) : (
                  <CheckoutLoginCreateAccountForm isLoading={false} />
                )}
              </StyledGrid>
            </Grid>
          ) : null}

          {Number(step) === ContactSteps.MessageSent ? (
            <Grid item xs={12} md={7} mb={10}>
              <StyledGrid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h5">Your Message has been Sent!</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">If you don&apos;t receive a response within 48 hours, please check your Junk Email box in case our response was filtered. Most responses are sent within a few hours of the inquiry, we hope you have an amazing vacation!</Typography>
                </Grid>
                <Grid item xs={12}>
                  {/* Do not use next router as it will send to catch-all route */}
                  <Button onClick={setPropertyUrl} color="secondary" fullWidth variant="contained">
                    Back to Property Listing
                  </Button>
                </Grid>
                <Grid item xs={12}><AppDivider /></Grid>

                <Grid item xs={12} mb={2}>
                  <Typography variant="h6">Booking on alluraDirect Saves you Money!</Typography>
                </Grid>

                <Grid item xs={12} sm={4} align="center">
                  <img src={checkoutRefer} className="img-responsive" height={150} />
                </Grid>

                <Grid item xs={12} sm={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        Did you know that by booking with alluraDirect, you can save up to 50% off service fees for the exact same
                        property on other hosting websites?
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        Help us by letting your friends know about alluraDirect. Same properties, less service fees.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} align="center" mt={1}>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=https%3A//${PRODUCTION_DOMAIN}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img src={facebookIcon} width="140px" />
                      </a>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} my={3}><CheckoutDownloadMobileApp /></Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2}>

                    <Grid item xs={12}>
                      <Typography variant="h6">Always Pay Securely through the Website</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">Every property has been verified. To confirm the security of your payment, always make sure to complete the process through the website. This will ensure your accommodation is fully confirmed and if there are any issues, you will have the support of the website.</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">If you are asked to complete the reservation on a different website or to make payment in any other manner than secured credit card through the website, please contact <a href={`mailto:${EMAIL_INFO}`}>{`${EMAIL_INFO}`}</a> or phone 604-707-6700 right away with the details.</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">Guest accounts will be suspended in the event of any reservation or request to process a payment outside of the website.</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2"><strong>There is a zero-tolerance stance on this policy.</strong> Stay secure, book on the website.</Typography>
                    </Grid>
                  </Grid>
                </Grid>


              </StyledGrid>
            </Grid>
          ) : null}

          {isMd ? (
            <Grid item xs={12}>
              <CheckoutSummaryCommonQuestions
                isLoading={isLoading}
                pid={propertyId}
              />
              <AppDivider marginVertical={'24px'} />
            </Grid>
          ) : null}

          {!isMd ? (
            <Grid item sm={12} md={5}>
              <StickyWrapper>
                <ContactSummary
                  pid={propertyId}
                  isLoading={isLoading}
                />
              </StickyWrapper>
            </Grid>
          ) : null}
        </Grid>
      </Container>
    </Page>
  );
};

export default Contact;
